// ADR 29.9.26al §2 — the page frame, in four widths (`full`: ADR 16.10.26o §1).
//
// The shell draws one around every outlet (its width is the route's
// `handle.frame`, `routeFrame.ts`); a page narrower than its route nests a
// second. A nested frame narrows a page and never widens it: it sits inside
// the shell's cell, whose cap already binds.
//
// **Identity contract.** The element type depends only on `as`. A new `width`
// — a navigation, a resize, a caller's `byWindowClass(…)` — re-renders the
// same node with a new `className` and `data-page-frame`, so its children keep
// their state. Changing `as` remounts the subtree, so a caller keeps it fixed.

import type { ReactNode } from 'react'

import { byWindowClass, type PerClass } from '../byWindowClass'
import type { WindowSizeClass } from '../useWindowClass'
import { useWindowClassValue } from '../windowClassContext'

export type PageWidth = 'form' | 'prose' | 'wide' | 'full'

// `prose` and `wide` are one string at every class: neither cap can bind below
// 600dp. `form` keeps today's `max-w-sm` at compact, so compact is unchanged.
// `full` is `wide` below expanded and the whole column at expanded, where the
// column's own padding is still the gutter (16.10.26o §1).
const FRAME_CLASSES: Record<PageWidth, PerClass<string>> = {
  form: { compact: 'mx-auto max-w-sm', medium: 'mx-auto max-w-md' },
  prose: { compact: 'mx-auto w-full max-w-3xl' },
  wide: { compact: 'mx-auto w-full max-w-6xl' },
  full: { compact: 'mx-auto w-full max-w-6xl', expanded: 'w-full' },
}

/** The frame's classes. Pure, so the table is pinned without a DOM. */
export function frameClass(width: PageWidth, sizeClass: WindowSizeClass): string {
  return byWindowClass(sizeClass, FRAME_CLASSES[width])
}

export interface PageFrameProps {
  width: PageWidth
  /** The six form pages keep their `<section>`. Keep it constant: a change remounts. */
  as?: 'div' | 'section'
  /** The page's own spacing, appended after the frame's classes. */
  className?: string
  'data-testid'?: string
  children?: ReactNode
}

export function PageFrame({ width, as: Tag = 'div', className, children, ...rest }: PageFrameProps) {
  const { sizeClass } = useWindowClassValue()
  const frame = frameClass(width, sizeClass)
  return (
    <Tag
      data-page-frame={width}
      data-testid={rest['data-testid']}
      className={className ? `${frame} ${className}` : frame}
    >
      {children}
    </Tag>
  )
}
